const COLUMN_ALIASES = {
  title: ["title", "naziv", "name", "titel"],
  address: ["address", "adresa", "adresse", "naslov"],
  phoneNumber: ["phone number", "phone", "telefon", "broj telefona", "telefonnummer", "telefonska številka"],
  email: ["e-mail", "email", "mail", "e-pošta"],
};

const HEADER_SEARCH_DEPTH = 10;

function normalize(value) {
  return String(value ?? "").trim().toLowerCase();
}

function findColumnIndexes(row) {
  const headers = row.map(normalize);
  return Object.fromEntries(
    Object.entries(COLUMN_ALIASES).map(([field, aliases]) => [
      field,
      headers.findIndex((header) => aliases.includes(header)),
    ]),
  );
}

const ZIP_AND_CITY = /^(\d{4,6})\s+(.+)$/;
const STREET_ZIP_AND_CITY = /^(.*?),?\s+(\d{4,6})\s+(\D.*)$/;

/**
 * Splits an address cell into street, zip code and city. Handles
 * "<street>, <zip> <city>" (e.g. "Sisačka 39, 10410 Velika Gorica"),
 * "<street>, <zip>, <city>" and a cell with no comma before the zip code.
 * Commas inside the street part are kept.
 */
export function parseAddress(value) {
  const text = String(value ?? "").trim();
  const parts = text.split(",").map((part) => part.trim()).filter(Boolean);
  const last = parts[parts.length - 1] ?? "";
  const zipAndCity = last.match(ZIP_AND_CITY);
  if (parts.length > 1 && zipAndCity) {
    return {
      streetAddress: parts.slice(0, -1).join(", "),
      zipCode: Number(zipAndCity[1]),
      city: zipAndCity[2],
    };
  }
  if (parts.length > 2 && /^\d+$/.test(parts[parts.length - 2])) {
    return {
      streetAddress: parts.slice(0, -2).join(", "),
      zipCode: Number(parts[parts.length - 2]),
      city: last,
    };
  }
  const inline = text.match(STREET_ZIP_AND_CITY);
  if (inline) {
    return {
      streetAddress: inline[1].trim(),
      zipCode: Number(inline[2]),
      city: inline[3].trim(),
    };
  }
  if (parts.length > 1) {
    return {
      streetAddress: parts.slice(0, -1).join(", "),
      zipCode: null,
      city: last,
    };
  }
  return { streetAddress: text, zipCode: null, city: "" };
}

export function clinicMatchKey(title, city) {
  return `${normalize(title)}|${normalize(city)}`;
}

/**
 * Reads the first sheet's rows (as returned by `sheet_to_json` with `header: 1`)
 * into clinic import rows. The header row is located by its column names, so
 * leading title rows and extra columns are ignored.
 * @throws {Error} with a translation key when the title or address column is missing
 */
export function parseClinicSpreadsheet(rows) {
  const headerIndex = rows
    .slice(0, HEADER_SEARCH_DEPTH)
    .findIndex((row) => {
      const columns = findColumnIndexes(row ?? []);
      return columns.title >= 0 && columns.address >= 0;
    });
  if (headerIndex < 0) {
    throw new Error("ClinicImportMissingColumns");
  }

  const columns = findColumnIndexes(rows[headerIndex]);
  const cell = (row, field) =>
    columns[field] >= 0 ? String(row[columns[field]] ?? "").trim() : "";

  return rows
    .slice(headerIndex + 1)
    .filter((row) => row && cell(row, "title"))
    .map((row, index) => ({
      id: `import-${index}`,
      title: cell(row, "title"),
      ...parseAddress(cell(row, "address")),
      phoneNumber: cell(row, "phoneNumber"),
      email: cell(row, "email"),
    }));
}
